import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, IconButton } from './Button';
import { StatusLabel, type Tone } from './Status';
import { IconClose, IconFile, IconUpload } from './icons';

/**
 * FileDrop: a drop zone plus the list of chosen files.
 *
 * - The zone is a dashed hairline, an icon and one sentence with a real
 *   "browse" button that opens the native picker. Dropping anywhere on the
 *   zone adds files. Accepted types and the size limit are the hint under
 *   the sentence — the only place they are stated.
 * - `compact` keeps an optional upload quiet: a small "Add a file" button
 *   that grows into the drop zone only while a file is dragged over the
 *   page, so it never competes with the required upload beside it.
 * - Each row: name · size · status word (dot + word) · remove. A failed
 *   file shows its reason under the row and stays in the list until the
 *   user removes it, so the error is never a toast.
 * - The component owns no validation or upload: the caller adds files
 *   (validating as it likes), sets each row's `status`/`error`, and removes.
 */
export type DropFileStatus =
  | 'checking'
  | 'ready'
  | 'uploading'
  | 'uploaded'
  | 'failed';

export interface DropFile {
  id: string;
  name: string;
  size: number;
  status: DropFileStatus;
  error?: string;
}

const STATUS: Record<
  DropFileStatus,
  { tone: Tone; hollow?: boolean; label: string }
> = {
  checking: { tone: 'neutral', hollow: true, label: 'Checking' },
  ready: { tone: 'success', label: 'Ready' },
  uploading: { tone: 'accent', hollow: true, label: 'Uploading' },
  uploaded: { tone: 'success', label: 'Uploaded' },
  failed: { tone: 'danger', label: 'Failed' },
};

/**
 * Whether a file is being dragged anywhere over the page. Enter/leave fire
 * for every element the drag crosses, so a depth count tells apart leaving
 * the window from moving between elements.
 */
function useWindowFileDrag(enabled: boolean) {
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const enter = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      depth += 1;
      setDragging(true);
    };
    const leave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const end = () => {
      depth = 0;
      setDragging(false);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', end);
    window.addEventListener('dragend', end);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', end);
      window.removeEventListener('dragend', end);
      setDragging(false);
    };
  }, [enabled]);
  return dragging;
}

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

export const FileDrop: React.FC<{
  files: DropFile[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  /** Native `accept` string. */
  accept?: string;
  /** Sentence under the prompt: accepted types and size limit. */
  hint?: string;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  /** A quiet "Add a file" button that becomes the zone only during a drag. */
  compact?: boolean;
  className?: string;
}> = ({
  files,
  onAdd,
  onRemove,
  accept,
  hint,
  multiple = true,
  disabled,
  label = 'Upload files',
  compact = false,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const hintId = useId();
  const dragging = useWindowFileDrag(compact && !disabled);
  const zone = !compact || dragging;

  const add = (list: FileList | null) => {
    if (!list || disabled) return;
    const arr = Array.from(list);
    if (arr.length) onAdd(multiple ? arr : arr.slice(0, 1));
  };

  return (
    <div className={className}>
      {zone ? (
        <div
          role="group"
          aria-label={label}
          aria-describedby={hint ? hintId : undefined}
          onDragOver={(e) => {
            if (disabled) return;
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            add(e.dataTransfer.files);
          }}
          className={clsx(
            'flex flex-col items-center justify-center rounded-v2-lg border border-dashed px-4 py-6 text-center transition-colors',
            compact && 'v2-rise-in',
            over || (compact && dragging)
              ? 'border-v2-accent bg-v2-accent/5'
              : 'border-v2-line-strong bg-v2-surface',
            disabled && 'opacity-60',
          )}
        >
          <span className="text-v2-fg-3" aria-hidden>
            <IconUpload size={18} />
          </span>
          <p className="mt-2 text-v2-13 text-v2-fg-2">
            Drop {multiple ? 'files' : 'a file'} here
            {!compact && (
              <>
                , or{' '}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => inputRef.current?.click()}
                  className="rounded-v2-sm font-medium text-v2-accent underline-offset-2 hover:underline disabled:cursor-not-allowed"
                >
                  browse
                </button>
              </>
            )}
          </p>
          {hint && (
            <p id={hintId} className="mt-1 text-v2-12 text-v2-fg-3">
              {hint}
            </p>
          )}
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          leading={<IconUpload size={13} />}
          disabled={disabled}
          title={hint}
          onClick={() => inputRef.current?.click()}
        >
          Add a file
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-label={label}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      {files.length > 0 && (
        <ul
          className="mt-3 divide-y divide-v2-line border-y border-v2-line"
          aria-label="Chosen files"
        >
          {files.map((f) => {
            const s = STATUS[f.status];
            return (
              <li key={f.id} className="py-2">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 text-v2-fg-3" aria-hidden>
                    <IconFile size={14} />
                  </span>
                  <span
                    className="min-w-0 flex-1 truncate text-v2-13 text-v2-fg"
                    title={f.name}
                  >
                    {f.name}
                  </span>
                  <span className="v2-num hidden shrink-0 text-v2-12 text-v2-fg-3 sm:inline">
                    {formatFileSize(f.size)}
                  </span>
                  <StatusLabel
                    tone={s.tone}
                    hollow={s.hollow}
                    size="sm"
                    className="w-[84px] shrink-0"
                  >
                    {s.label}
                  </StatusLabel>
                  <IconButton
                    label={`Remove ${f.name}`}
                    size="sm"
                    onClick={() => onRemove(f.id)}
                    disabled={disabled || f.status === 'uploading'}
                  >
                    <IconClose size={14} />
                  </IconButton>
                </div>
                {f.error && (
                  <p className="mt-1 pl-[26px] text-v2-12 text-v2-danger">
                    {f.error}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
